<template>
    <div>
        <div class="row">
            <div class="col-md-12">
                <form class="form">
                    <div class="form-actions">
                        <div class="row">
                            <div class="col-md-4">
                                <div class="form-group">
                                    <label for="examDate"><span class="required">*</span>{{ $t("labels.examDate") }}</label>
                                    <date-picker v-model="exam.date" width="100%" :appendToBody="true" format="DD/MM/YYYY" :lang="$i18n.locale"></date-picker>
                                </div>
                            </div>
                            <div class="col-md-8">
                                <div class="form-group">
                                    <label for="examType"><span class="required">*</span>{{ $t("labels.examType") }}</label>
                                    <select class="form-control" v-model="typeId">
                                        <option v-for="tp in examTypes" :value="tp._id" :key="tp._id">{{ tp.name[$i18n.locale] ? tp.name[$i18n.locale] : tp.name['en'] }}</option>
                                    </select>
                                </div>
                            </div>
                            <div class="col-md-12 bottom-space">
                                <label for="examNote">{{ $t("labels.notes") }}</label>
                                <textarea id="examNote" class="form-control" v-model="exam.notes"></textarea>
                            </div>
                            <div class="col-md-12">
                                <div class="dropzone bottom-space">
                                    <h5>
                                        * {{ $t("labels.examDocUpload") }}
                                        <span><strong>{{ $t('labels.maxVideoSize') }}:</strong><br>15 Mb</span>
                                        <span><strong>{{ $t('labels.allowedFormats') }}:</strong><br>{{ $store.state.allowedFileTypesSimple.toString().split(',').join(', ') }}</span>
                                    </h5> <br>
                                    <input type="file" id="file" ref="file" @change="getFile">
                                </div>
                                <div class="row bottom-space" v-if="exam.document.name">
                                    <div class="col-md-10">
                                        <span><strong>{{ $t('labels.name') }}:</strong> {{ exam.document.name }}</span> <br />
                                        <span :class="{red: exam.document.size/1024/1024 > 15}"><strong>{{ $t('labels.size') }}:</strong> {{ Math.round(exam.document.size/1024/1024) }} Mb</span> <br />
                                        <span :class="{red: !$store.state.allowedFileTypes.includes(exam.document.type)}"><strong>{{ $t('labels.type') }}:</strong> {{ exam.document.type }}</span>
                                    </div>
                                    <div class="col-md-2">
                                        <a class="warning p-0" @click.prevent="deleteDocument"><i class="fa fa-trash font-medium-3 mr-2"></i></a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button type="button" class="btn btn-success" @click.prevent="createExam">
                            <i class="icon-note"></i> {{ this.$t("labels.save") }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
        <div class="row">
          <div class="col-md-6">
              <div class="form-group">
                  <label for="entryFilter">{{ $t("labels.examTypeFilter") }}</label>
                  <select class="form-control" v-model="selectedType">
                      <option v-for="(type, index) in examTypes" :key="index" :value="type._id">
                        {{ type.name[$i18n.locale]}}
                      </option>
                  </select>
              </div>
          </div>
        </div>
        <div class="row">
            <div class="col-md-12">
                <table class="table table-responsive-sm">
                    <thead>
                        <th>
                          {{ $t("labels.examType") }}                            
                        </th>
                        <th>
                          {{ $t("labels.examDate") }}
                        </th>
                        <th>
                          {{ $t("labels.notes") }}
                        </th>
                        <th>
                          {{ $t("labels.actions") }}
                        </th>
                    </thead>
                    <tbody>
                        <tr v-for="e in exams" :key="e._id">
                            <td>
                                {{ e.type.name[$i18n.locale] ? e.type.name[$i18n.locale] : e.type.name['en'] }}
                            </td>
                            <td>
                                {{ formatDate(e.date) }}
                            </td>
                            <td>
                                {{ e.notes }}
                            </td>
                            <td>
                                <a v-if="e.document && e.document.signed_url" :href="e.document ? e.document.signed_url : ''" download><i class="fa fa-download font-medium-3 mr-2"></i></a>
                                <a class="warning p-0" @click.prevent="deleteExam(e)">
                                    <i class="fa fa-trash font-medium-3 mr-2"></i>
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import MedicalExamsService from '@/services/medicalExams'
import LovService from '@/services/lov'
import moment from 'moment'
import Progress from './Progress'

export default {
    props: ["patient"],
    components: {
        Progress
    },
    data() {
        return {
            errors: [],
            exams: [],
            typeId: '',
            examTypes: [],
            selectedType: null,
            exam: {
                date: moment(new Date()).format('YYYY-MM-DD'),
                notes: '',
                document: {},
                patientId: ''
            }
        }
    },
    computed: {
        examType() {
            let result = this.examTypes.find(t => t._id === this.typeId)
            let tpval = {
                id: result._id,
                type: result.type,
                name: result.name
            }
            return tpval
        }
    },
    methods: {
        createExam: async function() {
            this.errors = []
            let loader = this.$loading.show({
                color: '#ccc',
                loader: 'spinner'
            },{
                after: this.$createElement('Progress')
            })
            if (!this.exam.date) {
                this.errors.push(this.$t("errors.cantBeEmpty", [this.$t("labels.examDate")]))
            }
            if (!this.typeId || this.typeId.length === 0) {
                this.errors.push(this.$t("errors.cantBeEmpty", [this.$t("labels.examType")]))
            }
            if (this.exam.document && this.exam.document.type) {
                if (!this.$store.state.allowedFileTypes.includes(this.exam.document.type)) {
                    this.errors.push(this.$t("errors.wrongFiletype", [this.exam.document.name]))
                }
                let fileSizeMb = this.exam.document.size/1024/1024
                if (fileSizeMb > 15) {
                    this.errors.push(this.$t("errors.tooLarge", [this.exam.document.name]))
                }
            }
            if (this.errors.length === 0) {
                this.exam.type = this.examType
                let formData = new FormData()
                formData.append('document', this.exam.document)
                formData.append('exam', JSON.stringify(this.exam))
                let result = await MedicalExamsService.create(formData)
                if (result.data.success) {
                    this.$miniToastr.success(this.$t("flash.createSuccess", [this.$t("models.exam")]))
                    this.exam = {
                        document: {},
                        patientId: this.patient._id
                    }
                    await this.getExams()
                    this.$emit('updateexams')
                } else {
                    this.$miniToastr.error(this.$t("flash.createFailed", [this.$t("models.exam")]))
                }
            } else {
                this.$miniToastr.error(this.errors.toString().split(",").join("<hr/>"))
            }
            loader.hide()
            this.$store.state.currentProgress = 0
        },
        deleteExam: async function(exam) {
            let result = await MedicalExamsService.delete(exam)
            if (result.data.success) {
                this.$miniToastr.success(this.$t("flash.deleteSuccess", [this.$t("models.exam")]))
                await this.getExams()
                this.$emit('deleteexam', exam)
            } else {
                this.$miniToastr.error(this.$t("flash.deleteFailed", [this.$t("models.exam")]))
            }
        },
        getExams: async function() {
            let exams = await MedicalExamsService.all(this.patient._id, this.selectedType)
            if (exams) {
                this.exams = exams.data
            }
        },
        formatDate(d) {
            return moment(new Date(d)).format("YYYY-MM-DD")
        },
        getFile() {
            if (this.$refs.file.files.length > 0) {
                this.exam.document = this.$refs.file.files[0]
            }
        },
        deleteDocument() {
            this.$refs.file.value = ''
            this.exam.document = {}
        }
    },
    watch: {
      selectedType: function(_) {
        this.getExams()
      }
    },
    mounted: async function() {
        await this.getExams()
        let examTypes = await LovService.all('medical_exams', false)
        if (examTypes) {
            this.examTypes = examTypes.data
        }
        this.exam.patientId = this.patient._id
    }
}
</script>

<style lang="scss">
    form .form-actions {
        border-top: none !important;
    }
</style>


